import { Component, Inject, LOCALE_ID, Renderer2 } from '@angular/core';
import { ConfigService } from '../@vex/config/config.service';
import { Settings } from 'luxon';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@vex/services/navigation.service';
import { LayoutService } from '../@vex/services/layout.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SplashScreenService } from '../@vex/services/splash-screen.service';
import { VexConfigName } from '../@vex/config/config-name.model';
import { ColorSchemeName } from '../@vex/config/colorSchemeName';
import { MatIconRegistry, SafeResourceUrlWithIconOptions } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ColorVariable, colorVariables } from '../@vex/components/config-panel/color-variables';
import { IconService } from './services/icon-service.service';
import { filter, map } from 'rxjs';

@Component({
  selector: 'vex-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  constructor(private configService: ConfigService,
    private renderer: Renderer2,
    private platform: Platform,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private splashScreenService: SplashScreenService,
    private readonly matIconRegistry: MatIconRegistry,
    private titleService: Title,
    private readonly domSanitizer: DomSanitizer,) {
    Settings.defaultLocale = this.localeId;

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }
    this.matIconRegistry.addSvgIconResolver(
      (
        name: string,
        namespace: string
      ): SafeResourceUrl | SafeResourceUrlWithIconOptions | null => {
        switch (namespace) {
          case 'mat':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/material-design-icons/two-tone/${name}.svg`
            );

          case 'logo':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/logos/${name}.svg`
            );

          case 'flag':
            return this.domSanitizer.bypassSecurityTrustResourceUrl(
              `assets/img/icons/flags/${name}.svg`
            );
        }
      }
    );

    /**
     * Customize the template to your needs with the ConfigService
     * Example:
     *  this.configService.updateConfig({
     *    sidenav: {
     *      title: 'Custom App',
     *      imageUrl: '//placehold.it/100x100',
     *      showCollapsePin: false
     *    },
     *    footer: {
     *      visible: false
     *    }
     *  });
     */

    /**
     * Config Related Subscriptions
     * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
     * Example: example.com/?layout=apollo&style=default
     */
    this.activatedRoute.queryParamMap.subscribe(queryParamMap => {
      if (queryParamMap.has('layout')) {
        this.configService.setConfig(queryParamMap.get('layout') as VexConfigName);
      }

      if (queryParamMap.has('style')) {
        this.configService.updateConfig({
          style: {
            colorScheme: queryParamMap.get('style') as ColorSchemeName
          }
        });
      }

      if (queryParamMap.has('primaryColor')) {
        const color: ColorVariable = colorVariables[queryParamMap.get('primaryColor')];

        if (color) {
          this.configService.updateConfig({
            style: {
              colors: {
                primary: color
              }
            }
          });
        }
      }

      if (queryParamMap.has('rtl')) {
        this.configService.updateConfig({
          direction: coerceBooleanProperty(queryParamMap.get('rtl')) ? 'rtl' : 'ltr'
        });
      }
    });

    /**
     * Add your own routes here
     */
    this.navigationService.items = [

      {
        type: 'link',
        route: 'dashboard',
        label: 'Dashboard'
      },
      {
        type: 'dropdown',
        label: 'Examinations',
        children: [
          {
            type: 'link',
            route: "create_examination/0",
            label: "Create Examination",
            icon: 'add'
          },
          {
            type: 'link',
            route: "exams",
            label: "Examinations"
          },
          {
            type: 'link',
            route: "exam-groups",
            label: "Examination Groups"
          },
          {
            type: 'link',
            route: "exams/email-messages",
            label: "Send Email Messages"
          },
        ]
      },
      {
        type: 'dropdown',
        label: "Candidates",
        children: [
          {
            type: 'link',
            route: "candidates",
            label: "Candidates"
          },
          {
            type: 'link',
            route: "passport",
            label: "Upload Candidates Passport"
          },
        ]
      },
      {
        type: "dropdown",
        label: "Questions",
        children: [
          {
            type: 'link',
            route: "questions-bank",
            label: "Question Banks"
          },
          {
            type: 'link',
            route: "questioncategory",
            label: "Question Categories"
          },
          // {
          //   type: 'link',
          //   route: "questiongroups",
          //   label: "Question Groups"
          // },
        ]
      },
      {
        type: 'dropdown',
        label: 'Support Tickets',
        children: [
          {
            type: 'link',
            route: "complaints",
            label: "Tickets"
          },
          {
            type: 'link',
            route: "complaint-category",
            label: "Ticket Categories"
          },
        ]
      },
      {
        type: 'dropdown',
        label: 'Setup',
        children: [
          {
            type: 'link',
            route: "tempalates-download",
            label: "Data Templates"
          },
          {
            type: 'link',
            route: "company-settings",
            label: "Settings"
          },
        ]
      },

      // {
      //   type: 'link',
      //   route: "buyunits",
      //   label: "Buy Units"
      // },
      // {
      //   type: 'link',
      //   route: "faq",
      //   label: "FAQ"
      // },
      // {
      //   type: 'link',
      //   route: "contact",
      //   label: "Contact Us"
      // }
      
    ];
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setBrowserTitle();
      });
    this.setBrowserTitle();
  }
  setBrowserTitle() {
    let route = this.activatedRoute.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const rawTitle =
      route.snapshot.data?.['title'] ||
      route.snapshot.routeConfig?.title ||
      '';
    const finalTitle = rawTitle ? `WebTest - ${rawTitle}` : 'WebTest';
    setTimeout(() => {
      this.titleService.setTitle(finalTitle);
    });
  }
}
